import { create } from 'zustand'

export type VoiceState = 'idle' | 'listening' | 'processing'

interface VoiceStore {
  state: VoiceState
  transcript: string
  interimTranscript: string
  silenceMs: number
  isGenerating: boolean
  queuedTranscript: string
  setState: (s: VoiceState) => void
  setTranscript: (t: string) => void
  setInterim: (t: string) => void
  setSilence: (ms: number) => void
  setGenerating: (g: boolean) => void
  setQueued: (t: string) => void
  reset: () => void
}

export const useVoiceStore = create<VoiceStore>((set) => ({
  state: 'idle',
  transcript: '',
  interimTranscript: '',
  silenceMs: 0,
  isGenerating: false,
  queuedTranscript: '',
  setState: (state) => set({ state }),
  setTranscript: (transcript) => set({ transcript }),
  setInterim: (interimTranscript) => set({ interimTranscript }),
  setSilence: (silenceMs) => set({ silenceMs }),
  setGenerating: (isGenerating) => set({ isGenerating }),
  setQueued: (queuedTranscript) => set({ queuedTranscript }),
  reset: () => set({ state: 'idle', transcript: '', interimTranscript: '', silenceMs: 0, queuedTranscript: '' }),
}))

// ─── Audio Analysis (for orb visualization) ──────────────────
let audioContext: AudioContext | null = null
let analyser: AnalyserNode | null = null
let audioStream: MediaStream | null = null
let audioAnimFrame: number = 0
let currentAudioLevel = 0

export function getAudioLevel(): number { return currentAudioLevel }

function startAudioAnalysis() {
  navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
    console.log('[audio] analysis started')
    audioStream = stream
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    const source = audioContext.createMediaStreamSource(stream)
    analyser = audioContext.createAnalyser()
    analyser.fftSize = 256
    analyser.smoothingTimeConstant = 0.75
    source.connect(analyser)
    analyzeAudio()
  }).catch(e => { console.log('[audio] analysis failed:', e) })
}

function analyzeAudio() {
  if (!analyser) return
  const data = new Uint8Array(analyser.frequencyBinCount)
  analyser.getByteFrequencyData(data)
  let sum = 0
  for (let i = 0; i < data.length; i++) sum += data[i] * data[i]
  const rms = Math.sqrt(sum / data.length) / 255
  currentAudioLevel = Math.min(Math.pow(rms, 0.7) * 1.8, 1)
  audioAnimFrame = requestAnimationFrame(analyzeAudio)
}

function stopAudioAnalysis() {
  if (audioAnimFrame) cancelAnimationFrame(audioAnimFrame)
  if (audioStream) { audioStream.getTracks().forEach(t => t.stop()); audioStream = null }
  if (audioContext) { audioContext.close(); audioContext = null }
  analyser = null
  currentAudioLevel = 0
}

// ─── Config ──────────────────────────────────────────────────
const SILENCE_THRESHOLD = 2000
const WAKE_WORD = 'mike'

// Phrases that fully END the listening session (back to wake-word mode).
const END_SESSION_PHRASES = [
  'mike stop',
  'my stop',       // common misrecognition of "mike stop"
  'mic stop',
  'stop listening',
]

// Words that only INTERRUPT TTS but keep the mic listening for the next question.
// Matched as exact words so "look" / "don't stop" / "stop sign" don't trigger.
const INTERRUPT_TTS_WORDS = new Set(['stop', 'ok', 'okay'])

function isEndSessionCommand(text: string): boolean {
  const n = normalize(text)
  if (!n) return false
  return END_SESSION_PHRASES.some(p => n.includes(p))
}

function isInterruptTTSCommand(text: string): boolean {
  const n = normalize(text)
  if (!n) return false
  // Only match the bare word — "stop", "ok", "okay" alone (or repeated)
  const tokens = n.split(' ').filter(Boolean)
  if (tokens.length === 0 || tokens.length > 2) return false
  return tokens.every(t => INTERRUPT_TTS_WORDS.has(t))
}

// ─── State ───────────────────────────────────────────────────
let active = false          // main listening session is running
let isTTSSpeaking = false   // TTS is currently speaking
let lastAIResponse = ''     // normalized last AI reply (for echo detection)

type VoiceCallbacks = { onSend: (text: string) => Promise<void>; onStop: () => void }
let cbs: VoiceCallbacks | null = null
let finalText = ''
let lastSpeechAt = 0
let silenceInterval: ReturnType<typeof setInterval> | null = null
let currentRec: any = null

// Wake word state
let wakeWordActive = false
let wakeWordRec: any = null
let wakeWordCallbacks: VoiceCallbacks | null = null

// ─── Helpers ─────────────────────────────────────────────────
function normalize(text: string): string {
  return text.toLowerCase().replace(/[^a-z\s]/g, '').replace(/\s+/g, ' ').trim()
}

function isEcho(text: string): boolean {
  if (!lastAIResponse) return false
  const clean = normalize(text)
  if (!clean || clean.length < 10) return false
  if (clean === lastAIResponse) return true
  if (clean.length > 20 && lastAIResponse.startsWith(clean)) return true
  return false
}

function newRecognition(): any | null {
  const SR = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
  if (!SR) return null
  const r = new SR()
  r.lang = 'en-US'
  r.interimResults = true
  r.continuous = true
  r.maxAlternatives = 1
  return r
}

// ─── Main Voice Session ──────────────────────────────────────
function doStart() {
  if (!active) return
  if (currentRec) return

  const rec = newRecognition()
  if (!rec) return
  currentRec = rec

  rec.onresult = (event: any) => {
    if (!active) return

    // ── TTS is speaking: only listen for stop / end-session commands, ignore everything else
    if (isTTSSpeaking) {
      let heard = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        // Check interim AND final, so we react as fast as possible
        heard += event.results[i][0].transcript + ' '
      }
      const trimmed = heard.trim()

      // "mike stop" / "mic stop" / "stop listening" → END the session
      if (isEndSessionCommand(trimmed)) {
        console.log('[voice] end-session command during TTS:', trimmed)
        stopTTS()
        isTTSSpeaking = false
        stopVoiceSession()
        return
      }

      // "stop" / "ok" / "okay" → only interrupt TTS, keep listening
      if (isInterruptTTSCommand(trimmed)) {
        console.log('[voice] interrupt TTS:', trimmed)
        stopTTS()
        isTTSSpeaking = false
        finalText = ''
        lastSpeechAt = Date.now()
        useVoiceStore.getState().setTranscript('')
        useVoiceStore.getState().setInterim('')
        // recognition keeps running; mic stays active for the next question
        return
      }

      // Never transcribe anything else while TTS is reading
      return
    }

    // ── Normal listening mode
    let interim = ''
    let final = ''
    for (let i = event.resultIndex; i < event.results.length; i++) {
      if (event.results[i].isFinal) final += event.results[i][0].transcript
      else interim += event.results[i][0].transcript
    }

    // End-session command on interim → instant
    if (interim && isEndSessionCommand(interim)) {
      console.log('[voice] end-session command (interim):', interim)
      stopVoiceSession()
      return
    }

    if (interim) {
      lastSpeechAt = Date.now()
      useVoiceStore.getState().setInterim(interim)
    }

    if (final) {
      lastSpeechAt = Date.now()
      const trimmed = final.trim()

      if (isEndSessionCommand(trimmed)) {
        console.log('[voice] end-session command (final):', trimmed)
        stopVoiceSession()
        return
      }

      // A bare "stop" / "ok" / "okay" when TTS isn't speaking → just ignore
      // (don't add to the chat input)
      if (isInterruptTTSCommand(trimmed)) {
        console.log('[voice] interrupt word ignored (TTS not active):', trimmed)
        return
      }

      if (isEcho(trimmed)) {
        console.log('[voice] echo discarded:', trimmed)
        return
      }

      finalText = (finalText + ' ' + trimmed).trim()
      useVoiceStore.getState().setTranscript(finalText)
      useVoiceStore.getState().setInterim('')
    }
  }

  rec.onerror = () => {
    currentRec = null
    if (active) setTimeout(doStart, 300)
  }
  rec.onend = () => {
    currentRec = null
    if (active) setTimeout(doStart, 200)
  }

  try { rec.start() } catch { currentRec = null; setTimeout(doStart, 500) }
}

function checkSilence() {
  if (!active || !finalText.trim() || isTTSSpeaking) return
  if (isEcho(finalText)) {
    finalText = ''
    useVoiceStore.getState().setTranscript('')
    return
  }

  const elapsed = Date.now() - lastSpeechAt
  useVoiceStore.getState().setSilence(elapsed)
  if (elapsed < SILENCE_THRESHOLD) return

  const text = finalText.trim()
  finalText = ''
  useVoiceStore.getState().setTranscript('')
  useVoiceStore.getState().setInterim('')
  useVoiceStore.getState().setSilence(0)

  if (isEcho(text)) { console.log('[voice] echo blocked from send'); return }

  useVoiceStore.getState().setState('processing')
  useVoiceStore.getState().setGenerating(true)

  cbs?.onSend(text).finally(() => {
    useVoiceStore.getState().setGenerating(false)
    if (!active) return
    useVoiceStore.getState().setState('listening')
    const queued = useVoiceStore.getState().queuedTranscript.trim()
    if (queued) {
      finalText = queued
      useVoiceStore.getState().setQueued('')
      useVoiceStore.getState().setTranscript(queued)
      lastSpeechAt = Date.now()
    }
    if (!isTTSSpeaking) doStart()
  })
}

export function startVoiceSession(callbacks: VoiceCallbacks) {
  if (active) return
  active = true
  cbs = callbacks
  finalText = ''
  lastSpeechAt = Date.now()
  useVoiceStore.getState().setState('listening')
  useVoiceStore.getState().setTranscript('')
  useVoiceStore.getState().setInterim('')
  useVoiceStore.getState().setQueued('')
  startAudioAnalysis()
  // Stop wake word rec before starting main session
  if (wakeWordRec) { try { wakeWordRec.abort() } catch {} wakeWordRec = null }
  doStart()
  silenceInterval = setInterval(checkSilence, 200)
}

export function stopVoiceSession() {
  active = false
  cbs = null
  finalText = ''
  if (silenceInterval) { clearInterval(silenceInterval); silenceInterval = null }
  if (currentRec) { try { currentRec.abort() } catch {} currentRec = null }
  stopTTS()
  stopAudioAnalysis()
  useVoiceStore.getState().reset()
  // Resume wake word listening after stopping session
  if (wakeWordActive) setTimeout(doStartWakeWord, 1000)
}

export function isVoiceSupported(): boolean {
  return !!((window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition)
}

// ─── Wake Word ("mike") ──────────────────────────────────────
export function startWakeWordListening(callbacks: VoiceCallbacks) {
  if (wakeWordActive) return
  if (!isVoiceSupported()) return
  wakeWordActive = true
  wakeWordCallbacks = callbacks
  setTimeout(doStartWakeWord, 500)
}

export function stopWakeWordListening() {
  wakeWordActive = false
  wakeWordCallbacks = null
  if (wakeWordRec) { try { wakeWordRec.abort() } catch {} wakeWordRec = null }
}

export function isWakeWordActive(): boolean { return wakeWordActive }

function doStartWakeWord() {
  // Don't start wake word rec if already active, main session running, or TTS speaking
  if (!wakeWordActive || active || isTTSSpeaking) return
  if (wakeWordRec) return

  const SR = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
  if (!SR) return
  const rec = new SR()
  rec.lang = 'en-US'
  rec.interimResults = true
  rec.continuous = false
  rec.maxAlternatives = 3
  wakeWordRec = rec

  rec.onresult = (event: any) => {
    // Don't process wake word while TTS is speaking
    if (isTTSSpeaking) return
    for (let i = event.resultIndex; i < event.results.length; i++) {
      for (let j = 0; j < event.results[i].length; j++) {
        const t = event.results[i][j].transcript.toLowerCase().trim()
        if (t.includes(WAKE_WORD) || t.includes('mic') || t.includes('mikey')) {
          console.log('[wake] ACTIVATED!')
          try { rec.abort() } catch {}
          wakeWordRec = null
          if (wakeWordCallbacks) startVoiceSession(wakeWordCallbacks)
          return
        }
      }
    }
  }

  rec.onerror = () => {
    wakeWordRec = null
    if (wakeWordActive && !active) setTimeout(doStartWakeWord, 2000)
  }
  rec.onend = () => {
    wakeWordRec = null
    if (wakeWordActive && !active) setTimeout(doStartWakeWord, 1500)
  }

  try { rec.start() } catch { wakeWordRec = null; setTimeout(doStartWakeWord, 2000) }
}

// ─── TTS ─────────────────────────────────────────────────────
let ttsEnabled = true
let currentUtterance: SpeechSynthesisUtterance | null = null

export function setTTSEnabled(enabled: boolean) { ttsEnabled = enabled; if (!enabled) stopTTS() }
export function isTTSEnabled(): boolean { return ttsEnabled }

export function speakText(text: string) {
  if (!ttsEnabled || !window.speechSynthesis) return
  stopTTS()

  const cleaned = text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/#{1,3}\s*/g, '')
    .replace(/\/\/ FILE:.*/g, '')
    .replace(/\/\/ ACTION:.*/g, '')
    .trim()

  if (!cleaned) return
  const toSpeak = cleaned.length > 400 ? cleaned.slice(0, 400) + '...' : cleaned

  // Keep recognition running during TTS so we can hear stop commands.
  // The rec.onresult handler ignores everything except stop words while isTTSSpeaking.
  isTTSSpeaking = true
  finalText = ''
  useVoiceStore.getState().setTranscript('')
  useVoiceStore.getState().setInterim('')

  const utterance = new SpeechSynthesisUtterance(toSpeak)
  utterance.rate = 1.1
  utterance.pitch = 1.0

  // Load voices — may need a small delay on first call
  const voices = window.speechSynthesis.getVoices()
  const preferred = voices.find(v => v.name.includes('Google') && v.lang.startsWith('en'))
    ?? voices.find(v => v.lang.startsWith('en-US'))
  if (preferred) utterance.voice = preferred

  utterance.onend = () => {
    currentUtterance = null
    isTTSSpeaking = false
    finalText = ''
    lastSpeechAt = Date.now()
    useVoiceStore.getState().setTranscript('')
    useVoiceStore.getState().setInterim('')
    // Resume recognition after TTS finishes
    if (active && !currentRec) doStart()
    else if (!active && wakeWordActive && !wakeWordRec) doStartWakeWord()
  }

  utterance.onerror = () => {
    currentUtterance = null
    isTTSSpeaking = false
    if (active && !currentRec) doStart()
    else if (!active && wakeWordActive && !wakeWordRec) doStartWakeWord()
  }

  currentUtterance = utterance
  window.speechSynthesis.speak(utterance)
}

export function stopTTS() {
  window.speechSynthesis?.cancel()
  currentUtterance = null
  isTTSSpeaking = false
}

export function isSpeaking(): boolean {
  return isTTSSpeaking
}

export function setLastAIResponse(text: string) {
  lastAIResponse = normalize(text)
}

// ─── "Hold to Speak" — manual activation ─────────────────────
export function holdToSpeakStart() {
  if (!wakeWordActive && !active) return
  if (active) return
  if (wakeWordCallbacks) startVoiceSession(wakeWordCallbacks)
}
